import React from 'react';
import {
  Typography,
  Container,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Box,
  Stack
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BlogPage = () => {
    const blogPosts = [
        {
            id: 1,
            title: "Ask Your Protocol: How PCRAssist.com is Changing EMS Education",
            subtitle: "Introducing AI-powered, searchable, conversational protocols",
            preview: "What if you could upload your EMS protocol—and actually ask it questions like a teammate? That's exactly what PCRAssist.com/protocol offers. Our new Protocol Assistant gives medics and educators a powerful new tool...",
            date: "June 04, 2025",
            path: "/blog/protocol-assistant"
        },
        {
            id: 2,
            title: "Enhancing Patient Care Reports with AI",
            subtitle: "Augmenting, Not Replacing, EMS Documentation",
            preview: "In the high-stakes world of emergency medical services (EMS), accurate and timely documentation is crucial. Patient Care Reports (PCRs) serve as vital records that detail patient interactions...",
            date: "June 1, 2025",
            path: "/blog/enhancing-pcr-with-ai"
        },
        // Add more blog posts here as they become available
    ];

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'PCRAssist Blog',
        description: 'Latest articles and updates about PCR techniques, protocols, and AI assistance in molecular biology',
        url: 'https://pcrassist.com/blog',
        publisher: {
            '@type': 'Organization',
            name: 'PCRAssist',
            url: 'https://pcrassist.com',
            logo: {
                '@type': 'ImageObject',
                url: 'https://pcrassist.com/images/simple-medical-ai-logo.svg'
            }
        },
        blogPost: blogPosts.map(post => ({
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.preview,
            datePublished: post.date,
            url: `https://pcrassist.com${post.path}`
        }))
    };

    return (
        <>
            <SEO 
                title="Blog - PCAssist"
                description="Latest articles and updates about PCR techniques, protocols, and AI assistance in molecular biology"
                canonicalUrl="/blog"
                type="website"
                jsonLd={jsonLd}
            />
            <Container maxWidth="lg" sx={{ pt: 1, pb: 6 }}>
              <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                {/* Hero */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)',
                    border: '1px solid #e5e7eb',
                    borderRadius: 3,
                    p: { xs: 3, sm: 5 },
                    textAlign: 'center',
                    boxShadow: '0 6px 24px rgba(37,99,235,0.08)',
                    mt: 3,
                    mb: 4,
                  }}
                >
                  <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }} gutterBottom>
                    PCRAssist Blog
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Insights and updates on EMS documentation and AI
                  </Typography>
                </Box>

                {/* Posts Grid */}
                <Grid container spacing={3}>
                  {blogPosts.map((post) => (
                    <Grid item xs={12} md={6} key={post.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderRadius: 2,
                          transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                          '&:hover': {
                            borderColor: 'primary.light',
                            boxShadow: 3,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <CardActionArea component={Link} to={post.path} sx={{ height: '100%' }}>
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Typography variant="overline" color="primary" sx={{ letterSpacing: 1.2 }}>
                                Article
                              </Typography>
                              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                                {post.title}
                              </Typography>
                              <Typography variant="subtitle1" color="text.secondary">
                                {post.subtitle}
                              </Typography>
                              <Typography variant="body1" color="text.primary">
                                {post.preview}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {post.date}
                                </Typography>
                                <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  Read more <ArrowForward fontSize="small" />
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Container>
        </>
    );
};

export default BlogPage; 